import { FC, useContext, useEffect, useState, useRef, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import {
  useForm,
  Controller,
  FormProvider,
  SubmitHandler,
  Path,
} from "react-hook-form";
import __ from "src/utils/Translations";
import { Loader } from "src/Components/Loader/Loader";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { styles } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureSettings/structureSettingsStyle";
import AutocompleteManager from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteManager";
import AutocompleteEmployees from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteEmployees";
import AutocompleteMainArea from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteMainArea";
import AutocompleteAdditionalArea from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteAdditionalArea";
import useChangeArea from "src/Pages/Company/OrganizationStructure/api/useChangeArea";
import {
  Employee,
  IArea,
  ICreateArea,
  IFormData,
} from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { createEditAreaScheme } from "src/Pages/Company/OrganizationStructure/Areas/createEditArea.scheme";
import { dynamicNotification } from "src/utils/Notifications";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useDeleteArea from "src/Pages/Company/OrganizationStructure/api/useDeleteArea";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { organizationStructureAreaDelete } from "src/utils/RoleBasedSecurity/permissions";
import { getErrorMessages } from "src/Pages/Company/OrganizationStructure/utils/errors/errorMessages";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import {
  BackendError,
  handleBackendErrors,
} from "src/Pages/Company/OrganizationStructure/utils/errors/handleBackendErrors";
import useUnsavedChangesGuard from "src/Pages/Company/OrganizationStructure/utils/useUnsavedChangesGuard";
import { IStructureData } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/StructureData.schema";
import useAnimationFrame from "src/Pages/Company/OrganizationStructure/utils/useAnimationFrame";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";

interface IEditArea {
  areaData: IArea;
  exitEditMode: (...args: boolean[]) => void;
  structureId: string;
  structureData: IStructureData | undefined;
}

const EditArea: FC<IEditArea> = ({
  areaData,
  exitEditMode,
  structureId,
  structureData,
}) => {
  const { scrollTop } = useAnimationFrame();
  const companyId = useCompanyId();
  const { areaId } = useParams<{ areaId: string }>();
  const history = useHistory();
  const isAhr = useIsAhr();
  const isMember: boolean = areaData?.manager?.area?.id === areaId;
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const hasPermissionsStructureAreaDelete = usePermission(
    organizationStructureAreaDelete
  );

  const defaultOption: Employee | null = useMemo(() => {
    if (!areaData?.manager) {
      return {
        id: "structure",
        area: null,
        avatar: structureData?.avatarLink || null,
        initials: null,
        position: null,
        name: structureData?.name || null,
        fk: null,
        additionalDisplayArea: null,
        presenceInformation: null,
      };
    }

    return {
      id: areaData.manager.id,
      area: areaData.manager.area?.label || null,
      avatar: areaData.manager.avatar || null,
      initials: areaData.manager.initials || null,
      position: areaData.manager.position?.label || null,
      name: areaData.manager.name || null,
      fk: areaData.manager.fk || null,
      additionalDisplayArea: null,
      presenceInformation: areaData.manager.presenceInformation || null,
    };
  }, [areaData?.manager, structureData]);

  const defaultMainArea = useMemo(() => {
    if (!areaData.parent?.id) {
      return {
        id: null,
        name: structureData?.name,
        canBeParent: true,
      };
    }

    return {
      id: areaData.parent.id,
      name: areaData.parent.label,
      canBeParent: true,
    };
  }, [areaData.parent, structureData]);

  const defaultAdditionalArea = useMemo(
    () => ({
      id: areaData.additionalDisplayArea?.id || null,
      label: areaData.additionalDisplayArea?.label || "",
    }),
    [areaData.additionalDisplayArea]
  );

  const formMethods = useForm<IFormData>({
    resolver: standardSchemaResolver(createEditAreaScheme),
    mode: "all",
  });

  const unblockRef = useRef<(() => void) | null>(null);
  const nextLocationRef = useRef<any>(null);
  const proceedNavigationRef = useRef<(() => void) | null>(null);

  const { control, handleSubmit, setError, getValues, reset } = formMethods;

  useEffect(() => {
    if (areaData && structureData && defaultOption) {
      reset({
        name: areaData?.name ?? "",
        manager: defaultOption,
        description: areaData?.description ?? "",
        parent: defaultMainArea,
        additionalDisplayArea: defaultAdditionalArea,
        members: areaData?.members ?? [],
        managerAsMember: isMember,
      });
    }
  }, [
    areaData,
    structureData,
    defaultOption,
    defaultMainArea,
    defaultAdditionalArea,
    isMember,
    reset,
  ]);

  const { mutate, error } = useChangeArea(
    companyId,
    structureId!,
    areaId,
    exitEditMode
  );
  const logStatus = useLogStatus();

  useEffect(() => {
    if (!error) return;

    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "EditArea: changeArea failed",
      statusDetails: {
        source: "API",
        operation: "changeArea",
        status: (error as AxiosError)?.response?.status,
        url: (error as AxiosError)?.config?.url,
      },
      errorObject: error instanceof Error ? error : new Error(String(error)),
    });

    setIsFormSubmitted(false);

    const axiosError = error as AxiosError<{ errors: BackendError[] }>;
    const backendErrors = axiosError.response?.data?.errors || [];
    const processedErrors = handleBackendErrors({
      errors: backendErrors,
      errorMessages: getErrorMessages(),
    });

    processedErrors.forEach(({ field, message }) => {
      setError(field as Path<IFormData>, { type: "server", message });
    });
  }, [error, setError]);

  const confirmLeaving = useUnsavedChangesGuard({
    onExit: () => {
      if (proceedNavigationRef.current) proceedNavigationRef.current();
    },
    setModal,
    resetModal,
  });

  useEffect(() => {
    unblockRef.current = history.block((tx) => {
      if (isFormSubmitted) return undefined;

      const values = getValues();
      const hasUnsavedChanges = Object.values(values).some(
        (v) => v !== "" && v != null
      );

      if (!hasUnsavedChanges) return undefined;

      nextLocationRef.current = tx;

      proceedNavigationRef.current = () => {
        if (unblockRef.current) unblockRef.current();
        history.push(tx.pathname);
      };

      confirmLeaving();

      return false;
    });

    return () => {
      if (unblockRef.current) unblockRef.current();
    };
  }, [history, getValues, confirmLeaving, isFormSubmitted]);

  const onSubmit: SubmitHandler<IFormData> = (formData) => {
    const validationResult = createEditAreaScheme.safeParse(formData);

    if (!validationResult.success) {
      dynamicNotification(__("Błąd walidacji formularza"), "error");
      return;
    }

    const payload: ICreateArea = {
      ...formData,
      ...validationResult.data,
      manager:
        formData.manager?.id === "structure"
          ? null
          : formData.manager?.id ?? null,
      parent: formData.parent?.id || null,
      additionalDisplayArea: formData.additionalDisplayArea?.id || null,
      description: formData.description ?? null,
      members: formData.members?.map((member) => member.id) || [],
      inheritManager: formData.manager == null || formData.manager.id == null,
    };
    mutate(payload);
    scrollTop();
  };

  const handleDeleteArea = () => {
    setIsFormSubmitted(true);
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    deleteArea();
    resetModal();
  };

  const { mutate: deleteArea } = useDeleteArea({
    companyId,
    structureId,
    areaId,
  });

  if (!structureId) {
    return <Loader />;
  }

  return (
    <>
      <FormProvider {...formMethods}>
        <Paper
          sx={{
            ...styles.formWrapper,
            marginBottom: "1.4rem",
            padding: 4,
          }}
        >
          <Typography sx={{ fontSize: "20px" }} fontWeight="fontWeightBold">
            {__("Edytuj obszar")}
          </Typography>
          <Divider sx={{ marginBottom: "32px" }} />

          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <ControlledTextField
                name="name"
                label={__("Nazwa obszaru")}
                required
                normalizeSpaces
                maxLength={255}
              />

              <AutocompleteManager
                companyId={companyId!}
                structureId={structureId}
                helperText={__(
                  "Jeżeli pozostawisz to pole puste, to zarządzającym tego obszaru stanie się osoba zarządzająca nadrzędnym obszarem."
                )}
                isEdit
                dataTestId="os__edit-form-manager-input"
              />

              <AutocompleteMainArea
                companyId={companyId}
                structureId={structureId}
                areaId={areaId}
                dataTestId="os__edit-form-area-input"
              />

              <AutocompleteAdditionalArea
                companyId={companyId}
                structureId={structureId}
                dataTestId="os__edit-form-additional-area-input"
              />

              <Controller
                name="description"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <ControlledTextField
                    {...field}
                    data-testId="os__edit-form-description-input"
                    fullWidth
                    label={__("Opis")}
                    multiline
                    rows={4}
                    maxLength={1000}
                    showLimitAlways
                    sx={{ "& .MuiInputBase-input": { whiteSpace: "pre-wrap" } }}
                  />
                )}
              />

              <Typography fontWeight={700}>
                {__("Dodawanie pracowników")}:
              </Typography>

              <AutocompleteEmployees
                companyId={companyId}
                structureId={structureId}
                dataTestId="os__edit-form-employees-input"
              />

              <Divider />
              <Box sx={{ display: "flex", gap: "24px" }}>
                <Button
                  variant="outlined"
                  type="button"
                  size="large"
                  sx={{ flexGrow: 1 }}
                  onClick={() =>
                    history.push(
                      isAhr
                        ? `/ahr/organization-structure/configure-structure/structure-areas`
                        : `/company/edit/${companyId}/organization-structure/configure-structure/structure-areas`
                    )
                  }
                  data-testid="os__edit-form-cancel-button"
                >
                  {__("Anuluj")}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  size="large"
                  sx={{ flexGrow: 1 }}
                  data-testid="os__edit-form-save-button"
                >
                  {__("Zapisz zmiany")}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </FormProvider>
      {hasPermissionsStructureAreaDelete && (
        <Paper
          sx={{
            ...styles.formWrapper,
            marginBottom: "1.4rem",
            alignItems: "flex-start",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            padding: 4,
          }}
        >
          <Typography
            sx={{
              color: "#118CC6",
              textTransform: "uppercase",
              fontSize: "13px",
              fontWeight: 800,
              letterSpacing: 1.13,
              lineHeight: 1.5,
            }}
          >
            {__("Usuwanie Obszaru")}:
          </Typography>
          <Button
            data-testId="os__edit-form-delete-area"
            variant="text"
            onClick={() =>
              setModal({
                open: true,
                title: __("Usuń obszar"),
                description: __(
                  `Czy na pewno chcesz usunąć obszar ${areaData?.name}?`
                ),
                actions: [
                  {
                    label: __("Anuluj"),
                    variant: "outlined",
                    callbackFn: resetModal,
                  },
                  {
                    label: __("Tak, Usuń"),
                    color: "error",
                    variant: "contained",
                    callbackFn: handleDeleteArea,
                  },
                ],
              })
            }
            sx={{
              color: "#B91717",
              fontSize: "17px",
              lineHeight: 1.5,
              fontWeight: 400,
              padding: 0,
            }}
          >
            {__("Usuń obszar")}
          </Button>
        </Paper>
      )}
    </>
  );
};

export default EditArea;
